import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  AlertTriangle,
  BadgeCheck,
  CalendarCheck,
  CheckCircle2,
  ChevronRight,
  Eye,
  FileQuestion,
  History,
  Hourglass,
  ListTodo,
  Loader2,
  Mail,
  MessageSquare,
  Presentation,
  Repeat,
  Search,
  RefreshCw,
  Share2,
  Sparkles,
  UserPlus,
} from "lucide-react";

import {
  briefingLayout,
  LayoutSection,
  SectionGap,
  sectionGaps,
} from "./briefingLayout";
import { VERDICT_CLASS } from "./BriefingParts";
import BriefingProgress from "./BriefingProgress";
import ConfirmNote from "./ConfirmNote";
import {
  FLOW,
  PlanRef,
  SPONSOR_DRAFT,
  TEAM_STEP,
  planLabel,
  pubLabel,
} from "./flowCopy";
import PlanStateLabel from "./PlanStateLabel";
import GenerateDraftModal from "./ReviewGenerate";
import type { GenerateState } from "./ReviewGenerate";
import {
  OwnerChip,
  ReviewLensToggle,
  VerifyAllModal,
  VerifyMark,
  sectionTitle,
} from "./ReviewMarks";
import {
  ComingSoon,
  NextStepCard,
  pendingFor,
  PlanNav,
  shortDate,
  underReviewFor,
} from "./shared";
import {
  apiErrorMessage,
  useAccountPlanActions,
  useMyPlanActivity,
  useAccountPlanFile,
} from "../../Hooks/useAccountPlan";
import type {
  AccountPlanOverview,
  Cited,
  Persona,
  ReviewSummary,
  SectionOwner,
  SponsorView,
} from "../../Types/AccountPlan";
import { planValueText } from "../../Utils/AccountPlans/planDate";
import { toast } from "../../Utils/Helper/toast";

interface Props {
  accountId: string;
  data: AccountPlanOverview;
  nav: PlanNav;
  /** Refresh the page overview (step bar, review state) after a write. */
  onChanged: () => void;
  /** Jump to a cited slide in the viewer. */
  onCite: (slide: number) => void;
  onGoToPlan: () => void;
  /** Arrived via "Try again" elsewhere: restart a confirmed version's briefing with progress. */
  autoGenerate?: boolean;
  onAutoGenerated?: () => void;
}

/** Each section's number, title and CSM collapse, from the briefing's template (briefingLayout). */
const LayoutCtx = React.createContext<Record<string, LayoutSection>>({});
/** Briefing questions the plan leaves open, by the section they inform (sectionGaps). */
const GapsCtx = React.createContext<Record<string, SectionGap[]>>({});

/** Shown on every custom section, so a sponsor can tell it from the ExecBridge standard. */
const CUSTOM_NOTE = "Custom section · added for your program";

const todayLabel = () => shortDate(new Date().toISOString());

/** "Under review for 12 minutes", "… for 2 days · since Oct 30" (product review
 * 2026-11-01: the right unit, not "since today"). */
export const underReviewSince = (started?: string | null): string =>
  underReviewFor(started);

/** Review state and actions shared by every section card. */
interface ReviewCtxValue {
  review: ReviewSummary | null;
  owners: Record<string, SectionOwner | null>;
  persona: Persona;
  /** AE/CSM: the viewer is on the account team. */
  team: boolean;
  canAct: (role: Persona) => boolean;
  flash: string | null;
  busy: boolean;
  onVerify: (sid: string, role: SectionOwner) => void;
}
const ReviewCtx = React.createContext<ReviewCtxValue | null>(null);

const MEDDPICC_LABELS: Record<string, string> = {
  EB: "Economic Buyer",
  DM: "Decision Maker",
  CH: "Champion",
  EX: "Executive",
  IN: "Influencer",
  TB: "Technical Buyer",
  PR: "Procurement",
  IT: "IT Stakeholder",
  EU: "End User",
  NA: "Not Applicable",
};

const sentimentCls: Record<string, string> = {
  Positive: "bg-emerald-100 text-emerald-700",
  Neutral: "bg-gray-100 text-gray-700",
  Cautious: "bg-amber-100 text-amber-800",
  Negative: "bg-rose-100 text-rose-700",
};
const riskCls: Record<string, string> = {
  High: "text-rose-600",
  Medium: "text-amber-600",
  Low: "text-emerald-600",
};
const severityIcon: Record<string, string> = {
  high: "text-rose-500",
  medium: "text-amber-500",
  low: "text-gray-400",
};

/** A reader (not on the account) has no slides to open: citations only show the quote. */
const ReaderCtx = React.createContext(false);

/** Slide citation: hover shows the verbatim quote, click opens the slide. */
/** Briefing Questions on Review: verified like a section (backend review.BQ_ID). */
const BQ_SID = "bq";

/**
 * One chip per slide, in slide order (product review 2026-11-01): an answer quoting
 * slide 17 three times showed "slide 17" three times. Quotes from one slide are joined.
 */
export const slideCites = (
  evidence: { slide: number; quote: string }[]
): { slide: number; quote: string }[] => {
  const bySlide = new Map<number, string[]>();
  evidence.forEach(e => {
    const quotes = bySlide.get(e.slide) ?? [];
    if (e.quote && !quotes.includes(e.quote)) quotes.push(e.quote);
    bySlide.set(e.slide, quotes);
  });
  return Array.from(bySlide.entries())
    .sort(([a], [b]) => a - b)
    .map(([slide, quotes]) => ({ slide, quote: quotes.join(" … ") }));
};

const Chip: React.FC<{
  slide: number;
  quote?: string | null;
  onCite: (n: number) => void;
}> = ({ slide, quote, onCite }) =>
  React.useContext(ReaderCtx) ? (
    <span
      title={quote ? `“${quote}”` : undefined}
      className="mx-0.5 inline-flex shrink-0 items-center rounded bg-purple-50 px-1.5 py-0.5 align-baseline font-mono text-[11px] font-semibold text-[#8538d4]"
    >
      slide {slide}
    </span>
  ) : (
    <button
      type="button"
      onClick={() => onCite(slide)}
      title={quote ? `“${quote}” — open slide ${slide}` : `Open slide ${slide}`}
      className="mx-0.5 inline-flex shrink-0 items-center rounded bg-purple-50 px-1.5 py-0.5 align-baseline font-mono text-[11px] font-semibold text-[#8538d4] transition hover:-translate-y-px hover:bg-[#974ae4] hover:text-white"
    >
      slide {slide}
    </button>
  );

const Section: React.FC<{
  sid: string;
  title: string;
  wide?: boolean;
  slide?: number | null;
  onCite: (n: number) => void;
  children: React.ReactNode;
}> = ({ sid, title, wide, slide, onCite, children }) => {
  const ctx = useContext(ReviewCtx);
  const layout = useContext(LayoutCtx)[sid];
  const gaps = useContext(GapsCtx)[sid] || [];

  // Briefing Questions (sid "bq") are verified like a section but aren't numbered.
  const n = sid === BQ_SID ? null : (layout?.num ?? Number(sid.slice(1)));
  // The account header is titled with the account's name, whatever the template calls it.
  const shownTitle = sid === "s1" || !layout?.title ? title : layout.title;
  const persona = ctx?.persona || "ES";
  const review = ctx?.review || null;
  const owner = review ? ctx?.owners[sid] || null : null;
  const verification = owner ? review?.verifications[sid] : undefined;
  const published = !!review?.published;
  const pending =
    !!owner &&
    !!review &&
    !published &&
    !!review.progress[owner]?.pending.includes(sid);
  const team = !!ctx?.team;
  const mine = pending && team && owner === persona;
  const other = pending && team && !mine;
  const canAct = !!owner && !published && !!ctx?.canAct(owner);
  const overlays = (review?.overlays[sid] || []).slice();
  const isWide = wide || (persona === "CSM" && sid === "s8");
  const collapsed = persona === "CSM" && !!layout?.collapsedForCsm;

  const frame = mine
    ? "border-2 border-amber-300 bg-amber-50/30"
    : other
      ? "border-2 border-dashed border-gray-300 bg-white"
      : "border border-gray-200 bg-white";
  const cls = `scroll-mt-24 rounded-xl shadow-sm transition ${frame} ${
    isWide ? "lg:col-span-12" : "lg:col-span-6"
  } ${ctx?.flash === sid ? "ring-2 ring-amber-300" : ""}`;

  // Sections are verified as generated; AEs don't edit them (Sushant, 2026-09-28).
  const verb = owner === "CSM" ? "Confirm section" : "Verify section";

  const customNote = layout?.custom ? (
    <p className="-mt-2 mb-3 text-[11px] font-medium text-gray-500">
      {CUSTOM_NOTE}
    </p>
  ) : null;

  const extras = (
    <>
      {gaps.length > 0 && (
        <ul
          data-testid={`section-gaps-${sid}`}
          className="mt-3 space-y-1 rounded-lg border border-amber-200 bg-amber-50/60 px-3 py-2 text-xs text-amber-900"
        >
          {gaps.map(g => (
            <li key={g.questionId}>
              <span className="font-semibold">
                {g.status === "missing"
                  ? "Not in the plan:"
                  : "Partly answered:"}
              </span>{" "}
              {g.question}
            </li>
          ))}
        </ul>
      )}
      {overlays.map((o, i) => (
        <div
          key={`${o.at}-${i}`}
          className="mt-3 rounded-lg border border-purple-100 bg-purple-50/60 p-3 text-sm text-gray-700"
        >
          <span className="rounded bg-purple-100 px-2 py-0.5 font-mono text-[10px] font-semibold text-[#8538d4]">
            OVERLAY
          </span>
          <p className="mt-1.5 whitespace-pre-line">{o.text}</p>
          <div className="mt-1 text-[11px] text-gray-500">
            — {o.name || o.role} ({o.role}) · {shortDate(o.at)}
          </div>
        </div>
      ))}
      {/* The team's checking marks are for the review of a draft (UI tour item 14). */}
      {team && owner && !published && (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
          <OwnerChip owner={owner} />
          {canAct && (
            <span className="ml-auto flex flex-wrap items-center gap-2">
              {pending && (
                <button
                  type="button"
                  onClick={() => ctx?.onVerify(sid, owner)}
                  disabled={ctx?.busy}
                  className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 disabled:opacity-50"
                >
                  <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
                  {verb}
                </button>
              )}
            </span>
          )}
        </div>
      )}
    </>
  );

  // A published briefing shows none of the team's checking, to anyone (UI tour item
  // 14): the marks belong to a draft under review (the team's, and the sponsor's D8
  // labelled draft).
  const mark = !published ? (
    <VerifyMark sid={sid} owner={owner} verification={verification} />
  ) : null;

  if (collapsed) {
    return (
      <details id={`section-${sid}`} className={`group ${cls}`}>
        <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-xl p-5 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50">
          <span className="flex items-center gap-2">
            <ChevronRight
              className="h-4 w-4 text-gray-400 transition group-open:rotate-90"
              aria-hidden
            />
            <span className="text-base font-semibold text-gray-900">
              {n != null ? `${n} · ` : ""}
              {shownTitle}
            </span>
          </span>
          {mark}
        </summary>
        <div className="px-5 pb-5">
          {customNote}
          {slide ? (
            <div className="mb-3 flex justify-end">
              <Chip slide={slide} onCite={onCite} />
            </div>
          ) : null}
          {children}
          {extras}
        </div>
      </details>
    );
  }

  return (
    <section id={`section-${sid}`} className={`p-5 ${cls}`}>
      <div className="mb-3 flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold text-gray-900">
          {n != null ? `${n} · ` : ""}
          {shownTitle}
        </h3>
        <span className="flex flex-wrap items-center justify-end gap-1.5">
          {mark}
          {slide ? <Chip slide={slide} onCite={onCite} /> : null}
        </span>
      </div>
      {customNote}
      {children}
      {extras}
    </section>
  );
};

/** A rating the slide doesn't state: the model's judgement, shown as such (gap Q2). */
const AssessedTag: React.FC = () => (
  <span
    title="The plan doesn't state this rating; it's the AI's assessment of what the plan says."
    className="ml-1.5 inline-block rounded border border-gray-200 bg-gray-50 px-1 py-px align-middle text-[10px] font-medium uppercase tracking-wide text-gray-500"
  >
    AI-assessed
  </span>
);

const Empty: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-sm italic text-gray-400">{children}</p>
);

const CitedList: React.FC<{
  items: Cited[];
  onCite: (n: number) => void;
  /** One icon, or a set cycled by position (mock v4 cadence/asks). */
  icon?: React.ElementType | React.ElementType[];
  ordered?: boolean;
  empty: string;
}> = ({ items, onCite, icon, ordered, empty }) => {
  if (!items.length) return <Empty>{empty}</Empty>;
  return (
    <ul className="space-y-2 text-sm text-gray-700">
      {items.map((it, i) => {
        const Icon = Array.isArray(icon) ? icon[i % icon.length] : icon;
        return (
          <li key={i} className="flex items-start gap-2">
            {ordered ? (
              <span className="mt-0.5 w-4 shrink-0 font-mono text-xs font-semibold text-purple-500">
                {i + 1}.
              </span>
            ) : Icon ? (
              <Icon
                className="mt-0.5 h-3.5 w-3.5 shrink-0 text-purple-500"
                aria-hidden
              />
            ) : null}
            <span>
              {it.text}
              <Chip slide={it.slide} quote={it.quote} onCite={onCite} />
            </span>
          </li>
        );
      })}
    </ul>
  );
};

const Fact: React.FC<{
  label: string;
  children: React.ReactNode;
  slide?: number;
  quote?: string | null;
  onCite: (n: number) => void;
}> = ({ label, children, slide, quote, onCite }) => (
  <li className="flex items-start justify-between gap-3">
    <span className="text-gray-500">{label}</span>
    <span className="text-right font-medium text-gray-900">
      {children}
      {slide ? <Chip slide={slide} quote={quote} onCite={onCite} /> : null}
    </span>
  </li>
);

const Briefing: React.FC<{
  view: SponsorView;
  data: AccountPlanOverview;
  /** The plan number people see (D1), not the internal upload number. */
  fileVersion?: number | null;
  persona: Persona;
  onCite: (n: number) => void;
}> = ({ view, data, fileVersion, persona, onCite }) => {
  const [orgQuery, setOrgQuery] = useState("");
  const g = view.at_a_glance;
  const sponsors = data.sponsors.map(s => s.name).join(", ") || "Not assigned";
  const q = orgQuery.trim().toLowerCase();
  const risk = g.renewal_risk?.value;
  const renewals = g.renewals ?? [];

  const sections: Record<string, React.ReactNode> = {
    s1: (
      <Section sid="s1" title={data.account.name} wide onCite={onCite}>
        <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
          {[
            ["Sponsor", sponsors],
            ["Industry", data.account.industry || "—"],
            [
              "ARR",
              g.arr
                ? `${g.arr.value}${g.arr_target ? ` → ${g.arr_target.value}` : ""}`
                : "—",
            ],
            [
              "Plan refreshed",
              `${shortDate(view.generated_at)}${fileVersion != null ? ` · v${fileVersion}` : ""}`,
            ],
          ].map(([label, value]) => (
            <div key={label}>
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400">
                {label}
              </div>
              <div className="font-medium text-gray-900">{value}</div>
            </div>
          ))}
        </div>
      </Section>
    ),

    s2: (
      <Section sid="s2" title="Executive summary" wide onCite={onCite}>
        {view.executive_summary.text ? (
          <>
            <p className="text-sm leading-relaxed text-gray-700">
              {view.executive_summary.text}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-1 text-[11px] text-gray-500">
              <span className="rounded bg-purple-50 px-2 py-0.5 font-mono text-[10px] font-semibold text-[#8538d4]">
                SYNTHESIZED
              </span>
              <span className="ml-1">from</span>
              {view.executive_summary.slides.map(n => (
                <Chip key={n} slide={n} onCite={onCite} />
              ))}
            </div>
          </>
        ) : (
          <Empty>No summary could be grounded in the plan&apos;s slides.</Empty>
        )}
      </Section>
    ),

    s3: (
      <Section sid="s3" title="Account at a glance" onCite={onCite}>
        <ul className="space-y-2 text-sm">
          <Fact
            label="ARR"
            slide={g.arr?.slide}
            quote={g.arr?.quote}
            onCite={onCite}
          >
            {g.arr?.value || "—"}
          </Fact>
          <Fact
            label="Target"
            slide={g.arr_target?.slide}
            quote={g.arr_target?.quote}
            onCite={onCite}
          >
            {g.arr_target?.value || "—"}
          </Fact>
          <Fact
            label="Products"
            slide={g.products?.slide}
            quote={g.products?.quote}
            onCite={onCite}
          >
            {g.products?.items.join(", ") || "—"}
          </Fact>
          {renewals.length ? (
            <Fact label="Renewals" onCite={onCite}>
              <span className="flex flex-col items-end gap-1">
                {renewals.map(r => (
                  <span key={r.quote} data-testid="glance-renewal">
                    {planValueText(r.value)}
                    {r.label && (
                      <span className="font-normal text-gray-500">
                        {" "}
                        · {r.label}
                      </span>
                    )}
                    {r.risk && (
                      <span className={`ml-1 ${riskCls[r.risk]}`}>
                        ({r.risk})
                      </span>
                    )}
                    <Chip slide={r.slide} quote={r.quote} onCite={onCite} />
                  </span>
                ))}
              </span>
            </Fact>
          ) : (
            <Fact
              label="Renewal"
              slide={g.renewal_date?.slide}
              quote={g.renewal_date?.quote}
              onCite={onCite}
            >
              {planValueText(g.renewal_date?.value) || "—"}
              {risk && (
                <span className={`ml-1 ${riskCls[risk]}`}>({risk})</span>
              )}
            </Fact>
          )}
          <Fact
            label="Expansion"
            slide={g.expansion?.slide}
            quote={g.expansion?.quote}
            onCite={onCite}
          >
            {g.expansion?.value || "—"}
          </Fact>
        </ul>
      </Section>
    ),

    s4: (
      <Section sid="s4" title="Strategic priorities" onCite={onCite}>
        <CitedList
          items={view.priorities}
          onCite={onCite}
          ordered
          empty="No priorities stated in the plan."
        />
      </Section>
    ),

    s5: (
      <Section sid="s5" title="Stakeholder & power map" wide onCite={onCite}>
        {view.stakeholders.length ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {view.stakeholders.map(s => (
              <div
                key={s.name}
                className="rounded-lg border border-gray-200 p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold leading-tight text-gray-900">
                      {s.name}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {s.title}
                    </div>
                  </div>
                  <Chip slide={s.slide} quote={s.quote} onCite={onCite} />
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {s.deal_role && (
                    <span className="rounded bg-purple-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-purple-800">
                      {s.deal_role}
                    </span>
                  )}
                  {s.sentiment && (
                    <span
                      className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${sentimentCls[s.sentiment]}`}
                    >
                      {s.sentiment}
                    </span>
                  )}
                </div>
                {s.meddpicc.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {s.meddpicc.map(m => (
                      <span
                        key={m}
                        title={`MEDDPICC: ${MEDDPICC_LABELS[m] || m}`}
                        className="rounded border border-gray-300 px-1 font-mono text-[10px] font-semibold text-gray-600"
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <Empty>No stakeholders named in the plan.</Empty>
        )}
      </Section>
    ),

    s6: (
      <Section
        sid="s6"
        title="Org chart"
        wide
        slide={view.org_chart.slide}
        onCite={onCite}
      >
        {view.org_chart.entities.length ? (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:col-span-8">
              {view.org_chart.entities.map(e => {
                const hit =
                  q && `${e.name} ${e.title}`.toLowerCase().includes(q);
                return (
                  <div
                    key={e.name}
                    className={`rounded-md border px-3 py-2 text-xs transition ${hit ? "border-purple-500 bg-purple-50 ring-2 ring-purple-100" : "border-gray-200"} ${q && !hit ? "opacity-40" : ""}`}
                  >
                    <div className="font-semibold text-gray-900">{e.name}</div>
                    <div className="mt-0.5 text-gray-500">{e.title}</div>
                    {e.reports_to && (
                      <div className="mt-1 text-[10px] text-gray-400">
                        → {e.reports_to}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="lg:col-span-4">
              <label
                htmlFor="org-search"
                className="mb-2 block font-mono text-[10px] uppercase tracking-widest text-gray-400"
              >
                Search org chart
              </label>
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <input
                  id="org-search"
                  type="search"
                  value={orgQuery}
                  onChange={e => setOrgQuery(e.target.value)}
                  placeholder="Name or title…"
                  className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-100"
                />
              </div>
            </div>
          </div>
        ) : (
          <Empty>This plan has no org chart.</Empty>
        )}
      </Section>
    ),

    s7: (
      <Section sid="s7" title="Renewal & expansion outlook" onCite={onCite}>
        <div className="space-y-2 text-sm text-gray-700">
          <div>
            <span className="text-gray-500">
              {renewals.length ? "Renewal dates:" : "Renewal date:"}
            </span>{" "}
            <span className="font-semibold">
              {renewals.length
                ? renewals
                    .map(r => {
                      const v = planValueText(r.value);
                      return r.label ? `${v} (${r.label})` : v;
                    })
                    .join("; ")
                : planValueText(g.renewal_date?.value) || "—"}
            </span>
          </div>
          <div>
            <span className="text-gray-500">Risk:</span>{" "}
            <span className={`font-semibold ${risk ? riskCls[risk] : ""}`}>
              {risk || "—"}
            </span>
            {g.renewal_risk?.detail && (
              <span className="text-gray-500"> — {g.renewal_risk.detail}</span>
            )}
            {g.renewal_risk?.source === "assessed" && <AssessedTag />}
            {g.renewal_risk && (
              <Chip
                slide={g.renewal_risk.slide}
                quote={g.renewal_risk.quote}
                onCite={onCite}
              />
            )}
          </div>
          <div>
            <span className="text-gray-500">Expansion:</span>{" "}
            {g.expansion?.value || "—"}
          </div>
          {view.renewal_outlook && (
            <div className="border-t border-gray-100 pt-2">
              {view.renewal_outlook.text && (
                <p className="font-medium text-gray-900">
                  {view.renewal_outlook.text}
                  {view.renewal_outlook.slide && (
                    <Chip
                      slide={view.renewal_outlook.slide}
                      quote={view.renewal_outlook.quote}
                      onCite={onCite}
                    />
                  )}
                </p>
              )}
              {view.renewal_outlook.factors.length > 0 && (
                <div className="mt-2">
                  <CitedList
                    items={view.renewal_outlook.factors}
                    onCite={onCite}
                    empty=""
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </Section>
    ),

    s8: (
      <Section sid="s8" title="Risks & escalations" onCite={onCite}>
        {view.risks.length ? (
          <ul className="space-y-2 text-sm text-gray-700">
            {view.risks.map((r, i) => (
              <li key={i} className="flex items-start gap-2">
                <AlertTriangle
                  className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${severityIcon[r.severity]}`}
                  aria-label={`${r.severity} severity`}
                />
                <span>
                  {r.text}
                  {r.severity_source === "assessed" && <AssessedTag />}
                  <Chip slide={r.slide} quote={r.quote} onCite={onCite} />
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>No risks stated in the plan.</Empty>
        )}
      </Section>
    ),

    s9: (
      <Section
        sid="s9"
        title="Recommended cadence + asks for sponsor"
        wide
        onCite={onCite}
      >
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          <div>
            <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-gray-400">
              Recommended cadence
            </div>
            <CitedList
              items={view.cadence}
              onCite={onCite}
              icon={[Repeat, Presentation, Mail]}
              empty="The plan doesn't describe an engagement cadence."
            />
          </div>
          <div>
            <div className="mb-2 font-mono text-[10px] uppercase tracking-widest text-gray-400">
              Asks for sponsor
            </div>
            <CitedList
              items={view.asks}
              onCite={onCite}
              icon={[UserPlus, CalendarCheck, MessageSquare]}
              empty="No explicit asks for the sponsor in the plan."
            />
          </div>
        </div>
      </Section>
    ),
  };

  const layout = briefingLayout(view, persona);
  const byId: Record<string, LayoutSection> = {};
  layout.forEach(sec => {
    byId[sec.id] = sec;
  });

  return (
    <LayoutCtx.Provider value={byId}>
      <GapsCtx.Provider value={sectionGaps(view)}>
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
          {layout.map(sec => (
            <React.Fragment key={sec.id}>
              {sec.custom ? (
                <Section sid={sec.id} title={sec.title || ""} onCite={onCite}>
                  <CitedList
                    items={view.custom_sections?.[sec.id] || []}
                    onCite={onCite}
                    empty="Not covered in the plan."
                  />
                </Section>
              ) : (
                sections[sec.id]
              )}
            </React.Fragment>
          ))}
          {/* Verified like a section on Review (2026-10-31): the team only; the
              sponsor reads the answers on their own Briefing Questions tab. */}
          {persona !== "ES" &&
            data.sections.some(s => s.id === BQ_SID) &&
            !!view.briefing?.answers?.length && (
              <Section
                sid={BQ_SID}
                title={FLOW.briefingQuestions}
                wide
                onCite={onCite}
              >
                <ol className="space-y-3" data-testid="bq-review">
                  {view.briefing.answers.map(a => (
                    <li key={a.question_id} className="text-sm">
                      <div className="font-medium text-gray-900">
                        {a.question}
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-start gap-2 text-gray-700">
                        <span
                          className={`whitespace-nowrap rounded px-2 py-0.5 text-xs font-semibold ${VERDICT_CLASS[a.verdict]}`}
                        >
                          {a.verdict}
                        </span>
                        <span className="min-w-0 flex-1">
                          {a.direct_answer}
                        </span>
                        {slideCites(a.evidence).map(e => (
                          <Chip
                            key={`${a.question_id}-${e.slide}`}
                            slide={e.slide}
                            quote={e.quote}
                            onCite={onCite}
                          />
                        ))}
                      </div>
                    </li>
                  ))}
                </ol>
              </Section>
            )}
        </div>
      </GapsCtx.Provider>
    </LayoutCtx.Provider>
  );
};

/** The sponsor's link between the published briefing and a newer draft in review. */
export const SponsorDraftNote: React.FC<{
  draft: NonNullable<AccountPlanOverview["sponsor_draft"]>;
  data: AccountPlanOverview;
  nav: PlanNav;
}> = ({ draft, data, nav }) => {
  const v = draft.plan_version != null ? `v${draft.plan_version}` : "";
  const reading = data.current_file_id === draft.file_id;
  return (
    <div
      data-testid="sponsor-draft-note"
      data-reading={reading ? "draft" : "published"}
      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-700"
    >
      <span>{reading ? SPONSOR_DRAFT.reading(v) : SPONSOR_DRAFT.newer(v)}</span>
      <button
        type="button"
        onClick={() => nav.go("sponsor", { draft: reading ? null : "1" })}
        className="font-semibold text-[#8538d4] hover:underline"
      >
        {reading ? SPONSOR_DRAFT.backToPublished : SPONSOR_DRAFT.viewDraft}
      </button>
    </div>
  );
};

const SponsorViewTab: React.FC<Props> = ({
  accountId,
  data,
  nav,
  onChanged,
  onCite,
  onGoToPlan,
  autoGenerate,
  onAutoGenerated,
}) => {
  const { regenerateSponsorView, verifySections } = useAccountPlanActions();
  const { kick: kickMine, markSeen } = useMyPlanActivity(false);
  const fileId = data.current_file_id;
  // Set while a requested draft hasn't arrived: holds the generated_at of the
  // draft it replaces (null for the first), so "ready" means a new one landed,
  // whatever an older read of the version says.
  const [awaiting, setAwaiting] = useState<string | null | undefined>(
    undefined
  );
  const { file, loading, error, reload } = useAccountPlanFile(
    accountId,
    fileId,
    awaiting !== undefined
  );
  const [busy, setBusy] = useState(false);
  const [writing, setWriting] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [confirmAll, setConfirmAll] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const flashTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(flashTimer.current), []);
  const closeConfirm = useCallback(() => setConfirmAll(false), []);

  const persona = nav.persona;
  const team = persona !== "ES";
  const rawReview = file?.review || data.current_review || null;
  const review = rawReview && rawReview.status !== "none" ? rawReview : null;
  const published = !!review?.published;
  const owners = data.settings.section_owners;

  const afterWrite = useCallback(async () => {
    await reload();
    onChanged();
  }, [reload, onChanged]);

  const verify = useCallback(
    async (ids: string[], role: SectionOwner) => {
      if (!fileId || !ids.length) return;
      setWriting(true);
      setActionError(null);
      try {
        const res = await verifySections(accountId, fileId, ids, role);
        const left = res?.review
          ? pendingFor(res.review, role).length
          : Math.max(0, pendingFor(review, role).length - ids.length);
        const done = role === "CSM" ? "confirmed" : "verified";
        toast.success({
          title:
            ids.length === 1
              ? `${sectionTitle(data, ids[0])} ${done}`
              : `${ids.length} sections ${done}`,
          message: `The sponsor now sees "Verified by ${role} · ${todayLabel()}". ${
            left > 0
              ? `${left} of your sections left.`
              : "All your sections are done."
          }`,
        });
        await afterWrite();
      } catch (err) {
        const msg = apiErrorMessage(err, "Couldn't verify the section.");
        setActionError(msg);
        toast.error(msg);
      } finally {
        setWriting(false);
      }
    },
    [accountId, fileId, review, data, afterWrite]
  );

  const goToSection = (sid: string) => {
    const el = document.getElementById(`section-${sid}`);
    if (el) {
      if (el.tagName === "DETAILS") (el as HTMLDetailsElement).open = true;
      if (typeof el.scrollIntoView === "function")
        el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    window.clearTimeout(flashTimer.current);
    setFlash(sid);
    flashTimer.current = window.setTimeout(() => setFlash(null), 1600);
  };

  const regenerate = async (showProgress = false) => {
    if (!fileId) return;
    setBusy(true);
    setActionError(null);
    if (showProgress) setGenerating(true);
    // Before the request, so a regenerate never shows the old draft as ready.
    setAwaiting(file?.sponsor_view?.generated_at ?? null);
    try {
      await regenerateSponsorView(accountId, fileId);
      kickMine(); // the header chip and the "briefing ready" pop-up follow it
      await afterWrite();
    } catch (err) {
      setAwaiting(undefined);
      setActionError(
        apiErrorMessage(err, "Couldn't start generating the briefing.")
      );
    } finally {
      setBusy(false);
    }
  };

  const status = file?.sponsor_view_status;
  const view = file?.sponsor_view;
  // D47: while a first briefing is written the sponsor (and Preview as sponsor) sees
  // only that it is being written — not the team's step-by-step progress or a Draft.
  // One sponsor template (UI tour item 13): with no briefing to show, the same waiting
  // message whether it is being written, stalled or not started.
  const sponsorWaiting = !team && !!file && !view;
  const newDraft =
    !!view &&
    status !== "processing" &&
    // No earlier draft (null): any draft is the new one.
    (!awaiting || (view.generated_at ?? null) !== awaiting);
  const generateState: GenerateState =
    (status === "failed" || actionError) && !busy
      ? "failed"
      : newDraft
        ? "ready"
        : "running";
  const canRegenerate = nav.canAct("AE") && !published;
  // Confirm plan (Phase 2): the briefing is written only once the AE confirms the
  // version, and confirming starts it. Until then there is nothing to generate here.
  const unconfirmed =
    team && (data.current_confirmed === false || file?.confirmed === false);
  const mine = pendingFor(review, persona);
  const verb = persona === "CSM" ? "confirm" : "verify";
  const showWaiting = team && !!review && !published && !!view;

  const canAutoGenerate =
    !!autoGenerate &&
    canRegenerate &&
    !unconfirmed &&
    !view &&
    !!file &&
    status !== "processing" &&
    !loading &&
    !busy;
  useEffect(() => {
    if (!canAutoGenerate) return;
    onAutoGenerated?.();
    void regenerate(true);
    // regenerate is recreated each render; the flag is consumed once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoGenerate]);

  // Stop the extra polling once the new draft (or a failure) has arrived.
  const settled = generateState !== "running";
  useEffect(() => {
    if (settled) setAwaiting(undefined);
  }, [settled]);
  // Watched in the generate dialog: no "briefing ready" pop-up for it a moment later.
  useEffect(() => {
    if (generating && settled && fileId) markSeen(fileId, "brief");
  }, [generating, settled, fileId, markSeen]);

  // Only after every hook above: returning earlier changed the hook count the moment
  // the first version finished reading, and React crashed the page (spec PROC-03).
  if (!fileId) {
    // A version is uploaded but not read yet (spec M-1): don't ask for another upload.
    const reading = data.files.some(f => f.status === "processing");
    let message: React.ReactNode;
    if (reading && team) {
      message =
        "The account plan is being read. Once it finishes, the AE confirms it and the draft briefing is written.";
    } else if (reading) {
      message =
        "Your account team uploaded the account plan; the briefing appears here once they share it.";
    } else if (team && !nav.canAct("AE")) {
      // Only the AE uploads (UI tour item 11): anyone else on the team waits on them.
      message = `${FLOW.waitingAeUpload} the plan.`;
    } else if (team) {
      message = (
        <>
          The briefing is built from the account plan. Upload one from the{" "}
          <button
            type="button"
            onClick={onGoToPlan}
            className="text-[#974ae4] underline"
          >
            {TEAM_STEP.plan}
          </button>{" "}
          step.
        </>
      );
    } else {
      message =
        "The briefing is built from the account plan. It appears here once your account team shares one.";
    }
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center">
        <FileQuestion
          className="mx-auto mb-3 h-12 w-12 text-gray-300"
          aria-hidden
        />
        <div className="mb-1 text-lg font-semibold text-gray-700">
          {reading ? "Reading the plan…" : "No briefing to show yet"}
        </div>
        <p className="mx-auto max-w-md text-sm text-gray-500">{message}</p>
      </div>
    );
  }

  const ctx: ReviewCtxValue = {
    review,
    owners,
    persona,
    team,
    canAct: nav.canAct,
    flash,
    busy: writing,
    onVerify: (sid, role) => void verify([sid], role),
  };

  const started = review?.review_started_at || review?.generated_at;

  let next: React.ReactNode = null;
  if (!view && file && status !== "processing" && !loading) {
    // Mock v4: the no-draft state carries its own next step.
    next =
      persona === "ES" ? (
        <NextStepCard
          label="Next step"
          action={{
            label: "View Briefing Questions",
            onClick: () => nav.go("qa"),
          }}
        >
          Meanwhile, see which Briefing Questions the briefing will answer.
        </NextStepCard>
      ) : unconfirmed ? (
        nav.canAct("AE") ? (
          <NextStepCard
            label="Next step"
            action={{
              label: FLOW.confirmThePlan,
              onClick: () => nav.go("viewer"),
            }}
          >
            Check what ExecBridge read and confirm it. The draft briefing and
            Briefing Questions are written once you do.
          </NextStepCard>
        ) : (
          <NextStepCard label="Next step">
            {FLOW.waitingConfirmPlan}. The draft briefing is written once they
            do.
          </NextStepCard>
        )
      ) : canRegenerate ? null : (
        // Confirmed, no draft, not being written. A failed run reads as stalled (P8).
        <NextStepCard label="Next step">
          {status === "failed"
            ? "The draft briefing is taking longer than usual · Izba has been told."
            : "Waiting on the AE to write the draft briefing."}
        </NextStepCard>
      );
  } else if (view && persona === "ES") {
    next = (
      <NextStepCard
        label="Next step"
        action={{
          label: `Open ${FLOW.briefingQuestions}`,
          onClick: () => nav.go("qa"),
        }}
      >
        Want the question-by-question pre-meeting view? {FLOW.briefingQuestions}{" "}
        walks through each of your questions one at a time.
      </NextStepCard>
    );
  } else if (view && review && published) {
    next = (
      <NextStepCard
        label="Published"
        action={{
          label: "View publish record",
          onClick: () => nav.go("publish"),
        }}
      >
        The sponsor sees the published briefing. It re-enters review when a new
        plan version is confirmed.
      </NextStepCard>
    );
  } else if (view && review && mine.length === 0) {
    next =
      persona === "AE" ? (
        <NextStepCard
          label="AE next step"
          action={{ label: "Publish", onClick: () => nav.go("publish") }}
        >
          <span className="font-semibold">All your sections are verified.</span>{" "}
          Acknowledge any plan gaps on the checklist and publish to the sponsor.
        </NextStepCard>
      ) : (
        <NextStepCard
          label="CSM next step"
          action={{
            label: "View publish checklist",
            onClick: () => nav.go("publish"),
          }}
        >
          <span className="font-semibold">Your sections are confirmed.</span>{" "}
          The AE publishes the briefing to the sponsor.
        </NextStepCard>
      );
  }

  return (
    <ReaderCtx.Provider value={!!nav.reader}>
      <div className="space-y-4">
        {/* The AE's note from Confirm plan, for whoever reviews this briefing. */}
        {team && <ConfirmNote file={file} />}
        {/* During an update the sponsor keeps the published version; the newer draft
            is a link away, and from the draft a link back (board 02, 2026-10-31). */}
        {!team && data.sponsor_draft && (
          <SponsorDraftNote draft={data.sponsor_draft} data={data} nav={nav} />
        )}
        {team && (
          <div
            data-testid="review-header"
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm"
          >
            <div className="flex flex-wrap items-center gap-3">
              <ReviewLensToggle active="briefing" nav={nav} />
              {review && (
                <span className="text-xs text-gray-500">
                  {published
                    ? `Published briefing · ${pubLabel(review.published) || planLabel(review, "")}`
                    : `Draft from plan ${planLabel(review, "")}`.trim()}{" "}
                  · generated {shortDate(review.generated_at)}
                </span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs">
              {/* No Regenerate (product review 2026-11-01): rerunning the same file
                  mostly rewrites the same briefing and clears verifications. A bad
                  briefing is fixed by uploading a corrected deck; a failed or stalled
                  run keeps its own Try again below. The one rerun left: Briefing
                  Questions that couldn't be answered for this draft. */}
              {canRegenerate &&
                view &&
                (view.briefing_status === "failed" ||
                  !view.briefing?.answers?.length) && (
                  <button
                    type="button"
                    data-testid="rewrite-briefing"
                    onClick={() => void regenerate()}
                    disabled={busy || status === "processing"}
                    className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50"
                  >
                    <RefreshCw
                      className={`h-3.5 w-3.5 ${status === "processing" ? "animate-spin" : ""}`}
                      aria-hidden
                    />
                    {status === "processing"
                      ? "Writing…"
                      : FLOW.writeBriefingAgain}
                  </button>
                )}
              {/* Nothing to preview until there's a draft or a published briefing. */}
              {!nav.previewing && (view || data.last_published) && (
                <button
                  type="button"
                  onClick={nav.previewAsSponsor}
                  className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                >
                  <Eye className="h-3.5 w-3.5" aria-hidden />
                  Preview as sponsor
                </button>
              )}
              <ComingSoon label="Share" icon={Share2} />
            </div>
          </div>
        )}

        {showWaiting && (
          <div aria-live="polite">
            {mine.length > 0 ? (
              <div
                data-testid="waiting-on-you"
                className="flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 shadow-sm"
              >
                <ListTodo className="h-4 w-4 text-[#974ae4]" aria-hidden />
                {/* Of the viewer's own sections, so it adds up with the step bar's
                    "N of M verified" (all owners) and "AE a/b · CSM c/d" (UI tour item 6). */}
                <span className="font-semibold">
                  Waiting on you to {verb} ({mine.length} of{" "}
                  {review?.progress[persona as SectionOwner]?.owned ??
                    mine.length}
                  ):
                </span>
                {mine.map((sid, i) => (
                  <React.Fragment key={sid}>
                    {i > 0 && (
                      <span className="text-gray-300" aria-hidden>
                        ·
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => goToSection(sid)}
                      className="rounded font-medium text-[#8538d4] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                    >
                      {sectionTitle(data, sid)}
                    </button>
                  </React.Fragment>
                ))}
                {persona === "AE" && mine.length > 1 && (
                  <button
                    type="button"
                    onClick={() => setConfirmAll(true)}
                    disabled={writing}
                    className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 disabled:opacity-50"
                  >
                    <BadgeCheck className="h-3.5 w-3.5" aria-hidden />
                    Verify all ({mine.length})
                  </button>
                )}
              </div>
            ) : (
              <div
                data-testid="waiting-on-you"
                className="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 text-sm text-emerald-900"
              >
                <CheckCircle2
                  className="h-4 w-4 text-emerald-600"
                  aria-hidden
                />
                {persona === "AE" ? (
                  <span>
                    <span className="font-semibold">
                      All your sections are verified.
                    </span>{" "}
                    Next: publish the briefing to the sponsor (Publish step).
                  </span>
                ) : (
                  <span>
                    <span className="font-semibold">
                      Your sections are confirmed.
                    </span>{" "}
                    The AE publishes the briefing.
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {!team && !sponsorWaiting && (
          // The sponsor always reads the newest version, so the header says plainly
          // whether it is still a draft or published (Q1 = C + A, 2026-10-19). With no
          // draft generated yet it is the label alone, not a review banner.
          <div
            data-testid={review ? "review-status" : "sponsor-view-state"}
            className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm ${
              published
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border-amber-200 bg-amber-50 text-amber-900"
            }`}
          >
            {published ? (
              <span className="flex flex-wrap items-center gap-2">
                <PlanStateLabel state="published" showDate={false} />
                <BadgeCheck className="h-4 w-4" aria-hidden />
                Reviewed and published by{" "}
                {review?.published?.by_name || "the AE"} ·{" "}
                {shortDate(review?.published?.at)}
              </span>
            ) : (
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <PlanStateLabel state="draft" variant="banner" />
                {review && (
                  <span className="inline-flex items-center gap-1.5 text-xs text-amber-800">
                    <Hourglass className="h-3.5 w-3.5" aria-hidden />
                    {underReviewSince(started)}
                  </span>
                )}
              </span>
            )}
            {data.last_published && !published && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                <History className="h-3 w-3" aria-hidden />
                Last published: {pubLabel(data.last_published)} ·{" "}
                {shortDate(data.last_published.at)}
              </span>
            )}
          </div>
        )}

        {team && review && (
          <div
            data-testid="review-status"
            className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-2.5 text-sm ${
              published
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border-amber-200 bg-amber-50 text-amber-900"
            }`}
          >
            <span className="flex items-center gap-2">
              {published ? (
                <>
                  <BadgeCheck className="h-4 w-4" aria-hidden />
                  Reviewed and published by{" "}
                  {review.published?.by_name || "the AE"} ·{" "}
                  {shortDate(review.published?.at)}
                </>
              ) : (
                <>
                  <span className="rounded bg-amber-200 px-1.5 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-900">
                    Draft
                  </span>
                  <Hourglass className="h-4 w-4 text-amber-700" aria-hidden />
                  {underReviewSince(started)}
                </>
              )}
            </span>
            {data.last_published && !published && (
              <span className="inline-flex items-center gap-1 text-xs text-gray-600">
                <History className="h-3 w-3" aria-hidden />
                Last published: {pubLabel(data.last_published)} ·{" "}
                {shortDate(data.last_published.at)}
              </span>
            )}
          </div>
        )}

        {/* The legend explains a briefing's citations; with no briefing yet there's
            nothing to cite. */}
        {!sponsorWaiting && view && (
          <div
            data-testid="sources-legend"
            className="flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600"
          >
            <span className="font-semibold text-gray-700">Sources:</span>
            <span className="flex items-center gap-1.5">
              <span className="rounded bg-purple-50 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-[#8538d4]">
                slide N
              </span>
              {nav.reader
                ? "every item quotes the slide it came from — hover to read it"
                : "every item quotes the slide it came from — hover to read it, click to open it"}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="rounded bg-purple-50 px-2 py-0.5 font-mono text-[10px] font-semibold text-[#8538d4]">
                SYNTHESIZED
              </span>
              summary written from the cited slides
            </span>
          </div>
        )}
        {actionError && (
          <p role="alert" className="text-sm text-red-600">
            {actionError}
          </p>
        )}

        {loading && !file ? (
          <div
            className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500"
            role="status"
          >
            <Loader2
              className="h-5 w-5 animate-spin text-[#974ae4]"
              aria-hidden
            />{" "}
            Loading…
          </div>
        ) : error ? (
          <p role="alert" className="text-sm text-red-600">
            {error}
          </p>
        ) : view ? (
          <ReviewCtx.Provider value={ctx}>
            <Briefing
              view={view}
              data={data}
              fileVersion={file?.plan_version}
              persona={persona}
              onCite={onCite}
            />
          </ReviewCtx.Provider>
        ) : sponsorWaiting ? (
          <div
            data-testid="sponsor-briefing-writing"
            className="rounded-xl border border-gray-200 bg-white p-10 text-center"
          >
            <p className="text-base font-semibold text-gray-900">
              {FLOW.sponsorBriefingWriting}
            </p>
            <p className="mt-1 text-sm text-gray-500">
              {FLOW.sponsorBriefingWritingSub}
            </p>
          </div>
        ) : status === "processing" && file ? (
          <BriefingProgress file={file} />
        ) : (
          <div
            data-testid="no-draft"
            className="rounded-xl border border-dashed border-gray-300 bg-white p-10 text-center"
          >
            <Sparkles
              className="mx-auto mb-3 h-10 w-10 text-gray-300"
              aria-hidden
            />
            <div className="mb-1 text-lg font-semibold text-gray-800">
              {/* A failed run is the team's to retry; the sponsor only ever
                sees that the briefing is being prepared. */}
              {unconfirmed
                ? "The briefing is written after the plan is confirmed"
                : status === "failed" && team
                  ? "The draft briefing is taking longer than usual"
                  : team
                    ? "The draft briefing didn't start"
                    : "No draft briefing yet"}
            </div>
            <p className="mx-auto max-w-lg text-sm text-gray-500">
              {!team
                ? `Your account team ingested ${file?.plan_version != null ? `plan v${file.plan_version}` : "a new plan"} and is preparing the briefing.`
                : unconfirmed
                  ? `${PlanRef(file)} was read${
                      file?.processed_at
                        ? ` on ${shortDate(file.processed_at)}`
                        : ""
                    }. Nothing is written until the AE confirms it's the right plan.`
                  : status === "failed"
                    ? // P8 (Sushant, 2026-11-03): no "Try again"; it reads as stalled.
                      `Izba has been told. There is nothing for you to do; the plan text is still available in ${FLOW.confirmPlan}.`
                    : `${canRegenerate ? "Write it now." : "The account executive can write it."} The plan text is available in ${FLOW.confirmPlan}.`}
            </p>
            {/* No first-time "Generate": confirming the plan starts the briefing.
              "Try again" restarts a confirmed version's briefing that failed
              (or, for a version confirmed before Phase 2, never started). */}
            {canRegenerate && !unconfirmed && status !== "failed" && (
              <button
                type="button"
                onClick={() => void regenerate(true)}
                disabled={busy || !file?.parsed}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-[#974ae4] px-4 py-2 text-sm font-semibold text-white hover:bg-[#8538d4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" aria-hidden />
                Write the briefing
              </button>
            )}
          </div>
        )}

        {next}

        {generating && file && (
          <GenerateDraftModal
            version={file.plan_version ?? null}
            filename={file.filename}
            slides={file.page_count}
            state={generateState}
            error={actionError}
            questions={data.question_set?.questions.length || 0}
            sponsorName={data.sponsors[0]?.name}
            coverage={view?.briefing?.coverage}
            queue={data.files.find(f => f.id === file.id)?.queue?.brief}
            onClose={() => setGenerating(false)}
            onReview={() => setGenerating(false)}
          />
        )}

        {confirmAll && (
          <VerifyAllModal
            titles={mine.map(sid => sectionTitle(data, sid))}
            today={todayLabel()}
            busy={writing}
            onCancel={closeConfirm}
            onConfirm={() => {
              setConfirmAll(false);
              void verify(mine, "AE");
            }}
          />
        )}
      </div>
    </ReaderCtx.Provider>
  );
};

export default SponsorViewTab;
